import axios, { AxiosInstance, AxiosPromise } from 'axios'
import _ from 'lodash'
import Vue from 'vue'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'

import getters from '@/store/interview/bookings/getters'
import { IdObject, ModuleState, params, RootState, types } from './types'

export function postgrestFactory(defaultData: {
  baseUrl: string
  namespaced?: boolean
  pullOnSignIn?: boolean
  skipWhenSaving?: string[]
  extend?: {
    actions?: ActionTree<ModuleState, RootState>
    mutations?: MutationTree<ModuleState>
    getters?: GetterTree<ModuleState, RootState>
    state?: ModuleState
  }
}): Module<ModuleState, RootState> {
  return {
    actions: {
      ...defaultData.extend?.actions,
      USER_LOGGED_OUT: {
        root: true,
        handler({ commit }) {
          commit({ type: 'resetState' })
        },
      },
      USER_SIGNED_IN: {
        root: true,
        handler({ dispatch }) {
          if (defaultData.pullOnSignIn) {
            dispatch({ type: types.actions.PullObjects })
          }
        },
      },
      async [types.actions.PullObjects](
        { commit, rootGetters },
        { where, setLoading = true }: params.actions.PullObjects
      ) {
        if (setLoading) {
          commit('setLoading', true)
        }
        const http = rootGetters.http as AxiosInstance
        try {
          const { data } = await http.get<IdObject[]>(defaultData.baseUrl)
          commit({
            objects: data,
            type: types.mutations.AddObjects,
          })
          return data
        } catch (error) {
          console.log(error)
        } finally {
          if (setLoading) {
            commit('setLoading', false)
          }
        }
      },
      async [types.actions.PushObject](
        { commit, rootGetters },
        { object, updateInstance = false }: params.actions.PushObject
      ) {
        // UPSERT: http://postgrest.org/en/v5.2/api.html#upsert
        return (rootGetters.http as AxiosInstance)
          .post<IdObject[]>(defaultData.baseUrl, _.omit(object, defaultData.skipWhenSaving), {
            headers: {
              Prefer: 'return=representation, resolution=merge-duplicates',
            },
          })
          .then(({ data }) => {
            const upsertedInstance = data[0]

            commit({
              objects: data,
              type: types.mutations.AddObjects,
            })

            if (updateInstance) {
              _.merge(object, upsertedInstance)
            }

            return upsertedInstance
          })
          .catch(error => {
            console.error(error)
            throw error
          })
      },
      async [types.actions.DropObject](
        { commit, rootGetters },
        { object }: params.actions.DropObject
      ) {
        // UPSERT: http://postgrest.org/en/v5.2/api.html#upsert
        return (rootGetters.http as AxiosInstance)
          .delete(defaultData.baseUrl, { params: { id: `eq.${object.id}` } })
          .then(({ data }) => {
            commit({
              objects: [object],
              type: types.mutations.RemoveObjects,
            })
          })
      },
    },
    getters: {
      ...defaultData.extend?.getters,
      loading: state => state.loading,
      storedObjects: state => state.storedObjects,
      storedObjectsArray: state => Object.values(state.storedObjects),
      findBy(state, gtrs) {
        return (predicate: any) => {
          return _.find(gtrs.storedObjectsArray, predicate)
        }
      },
      filterBy(state, gtrs) {
        return (predicate: any) => {
          return _.filter(gtrs.storedObjectsArray, predicate)
        }
      },
    },
    mutations: {
      ...defaultData.extend?.mutations,
      setLoading(state, payload: boolean) {
        state.loading = payload
      },
      [types.mutations.AddObjects](state, { objects }: params.mutations.AddObjects) {
        objects.forEach(object => {
          Vue.set(state.storedObjects, object.id, object)
        })
      },
      [types.mutations.RemoveObjects](state, { objects }: params.mutations.RemoveObjects) {
        objects.forEach(object => {
          Vue.delete(state.storedObjects, typeof object === 'string' ? object : object.id)
        })
      },
      resetState(state) {
        state.loading = false
        state.storedObjects = {}
      },
    },
    namespaced : true,
    state: {
      loading: false,
      ...defaultData.extend?.state,
      storedObjects: {},
    },
  }
}
